import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, ShieldCheck } from "lucide-react";

const SECTIONS = [
  {
    n: "1", title: "SelLend Is a Marketplace",
    body: "SelLend provides a platform that connects users.",
    doesTitle: "SelLend:", does: ["Facilitates listings", "Facilitates payments", "Provides dispute reporting tools", "Helps users connect"],
    doesntTitle: "SelLend does not:", doesnt: ["Own listed items", "Store listed items", "Inspect every item", "Guarantee item condition", "Guarantee user behaviour", "Act as a buyer, seller, lender, renter, insurer, or carrier"],
    footer: "All transactions occur directly between users.",
  },
  { n: "2", title: "User Responsibility", body: "Users are solely responsible for:", list: ["Information they provide", "Listings they create", "Rental agreements entered into", "Sales agreements entered into", "Item condition", "Collection and return arrangements", "Compliance with local laws"] },
  { n: "3", title: "Item Ownership", body: "Users may only list items that:", list: ["They legally own", "They have permission to rent or sell", "Are not subject to theft, fraud, or legal restrictions"], footer: "SelLend may remove listings that appear suspicious or unlawful." },
  {
    n: "4", title: "Accuracy Of Listings",
    doesTitle: "Listing owners must provide:", does: ["Accurate descriptions", "Accurate photographs", "Known defects or issues", "Honest pricing"],
    doesntTitle: "Users may not:", doesnt: ["Misrepresent item value", "Hide damage", "Use misleading images", "Provide fraudulent information"],
  },
  {
    n: "5", title: "Rental Responsibilities",
    doesTitle: "Renters agree to:", does: ["Use items safely", "Follow manufacturer instructions", "Return items on time", "Return items in substantially the same condition received"],
    doesntTitle: "Owners agree to:", doesnt: ["Provide items that are fit for intended use", "Disclose known defects", "Honour accepted bookings"], doesntPositive: true,
  },
  { n: "6", title: "Security Deposits", body: "Security deposits are intended to help cover:", list: ["Damage", "Loss", "Missing parts", "Non-return"], footer: "Security deposits do not guarantee recovery of all losses. SelLend may assist with dispute administration, but responsibility ultimately remains with participating users." },
  { n: "7", title: "Disputes", body: "Where disputes arise, users should first attempt to resolve issues directly, and may submit disputes through SelLend. SelLend may review supporting information and reserves the right to determine platform actions including:", list: ["Account warnings", "Temporary suspension", "Permanent removal"], footer: "SelLend is not obligated to act as an arbitrator, mediator, insurer, or court." },
  { n: "8", title: "No Financial Guarantee", body: "SelLend does not guarantee:", list: ["Rental payments", "Item condition", "User behaviour", "Recovery of losses", "Recovery of stolen items", "Collection of debts between users"], footer: "Users participate at their own risk." },
  { n: "9", title: "Fraud Prevention", body: "SelLend reserves the right to:", list: ["Verify identities", "Request additional documentation", "Suspend suspicious accounts", "Cancel suspicious transactions", "Cooperate with law enforcement where legally required"] },
  { n: "10", title: "Prohibited Items", body: "The following are prohibited:", list: ["Illegal items", "Counterfeit goods", "Stolen property", "Weapons where prohibited by law", "Hazardous materials", "Items requiring licences where not legally permitted", "Any item prohibited by applicable law"], footer: "SelLend may remove prohibited listings without notice." },
  {
    n: "11", title: "User Conduct",
    doesTitle: "Users must:", does: ["Act honestly", "Communicate respectfully", "Honour agreements", "Follow platform policies"],
    doesntTitle: "Users must not:", doesnt: ["Harass others", "Threaten others", "Defraud others", "Manipulate reviews", "Circumvent platform fees", "Conduct transactions using false identities"],
  },
  { n: "12", title: "Reviews & Reputation", body: "Reviews help maintain trust within the community. SelLend may:", list: ["Remove fake reviews", "Remove abusive reviews", "Suspend users attempting to manipulate ratings"], footer: "Repeated poor behaviour may result in account restrictions." },
  { n: "13", title: "Limitation Of Liability", body: "To the fullest extent permitted by law, SelLend shall not be responsible for:", list: ["Damage to property", "Injury arising from item use", "Theft", "Loss of earnings", "Missed bookings", "Fraud by users", "Actions of buyers, sellers, renters, or owners"], footer: 'The platform is provided on an "as available" basis.' },
  { n: "14", title: "Right To Remove Users", body: "SelLend reserves the right to:", list: ["Remove listings", "Restrict functionality", "Suspend accounts", "Permanently ban users where such actions are considered necessary to protect the marketplace and its users."] },
  { n: "15", title: "Protecting The SelLend Community", body: "The long-term success of SelLend depends on trust. We expect all users to:", list: ["Act fairly", "Respect others", "Honour agreements", "Support a safe marketplace"], footer: "Users who undermine trust may lose access to the platform." },
];

const PosList = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((d) => (
      <li key={d} className="flex items-start gap-3 text-sm leading-relaxed">
        <span className="mt-0.5 w-5 h-5 rounded-full bg-[#E3F0E3] flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-[#2E7D32]" /></span>
        {d}
      </li>
    ))}
  </ul>
);

const NegList = ({ items }) => (
  <ul className="space-y-2.5">
    {items.map((d) => (
      <li key={d} className="flex items-start gap-3 text-sm leading-relaxed">
        <span className="mt-0.5 w-5 h-5 rounded-full bg-red-50 flex items-center justify-center shrink-0"><X className="w-3.5 h-3.5 text-red-600" /></span>
        {d}
      </li>
    ))}
  </ul>
);

const Bullets = ({ items }) => (
  <ul className="space-y-2 mt-3">
    {items.map((d) => (
      <li key={d} className="flex items-start gap-3 text-sm leading-relaxed text-muted-foreground">
        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#FF5A00] shrink-0" />
        <span className="text-[#0A0A0A]">{d}</span>
      </li>
    ))}
  </ul>
);

export default function Policy() {
  return (
    <div data-testid="policy-page" className="min-h-screen">
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-16 pb-10">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[#FF5A00] mb-5">
            <ShieldCheck className="w-4 h-4" /> Marketplace Policy
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[0.95]">SelLend Marketplace Policy</h1>
          <p className="text-sm text-muted-foreground mt-4">Last updated: {new Date().toLocaleDateString("en-IE", { year: "numeric", month: "long", day: "numeric" })}</p>
          <div className="mt-8 bg-[#FFF0E8] border border-[#FF5A00]/20 rounded-2xl p-6">
            <h2 className="font-semibold text-lg">Our Mission</h2>
            <p className="text-muted-foreground text-sm mt-2 leading-relaxed">
              SelLend exists to help people rent, lend, buy, and sell items safely within their local communities. To protect our users and maintain trust in the marketplace, all users must comply with this Marketplace Policy.
            </p>
          </div>
        </motion.div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16 space-y-10">
        {SECTIONS.map((s) => (
          <div key={s.n} data-testid={`policy-section-${s.n}`} className="scroll-mt-24">
            <h2 className="font-display text-2xl font-bold tracking-tight flex items-baseline gap-3">
              <span className="text-[#FF5A00] text-lg font-bold">{s.n}</span>{s.title}
            </h2>
            {s.body && <p className="text-muted-foreground text-sm mt-3 leading-relaxed">{s.body}</p>}
            {s.list && <Bullets items={s.list} />}
            {(s.does || s.doesnt) && (
              <div className="grid sm:grid-cols-2 gap-5 mt-4">
                {s.does && (
                  <div className="bg-white border border-black/10 rounded-2xl p-5">
                    <h3 className="font-semibold text-sm text-[#2E7D32] mb-3">{s.doesTitle}</h3>
                    <PosList items={s.does} />
                  </div>
                )}
                {s.doesnt && (
                  <div className="bg-white border border-black/10 rounded-2xl p-5">
                    <h3 className={`font-semibold text-sm mb-3 ${s.doesntPositive ? "text-[#2E7D32]" : "text-red-600"}`}>{s.doesntTitle}</h3>
                    {s.doesntPositive ? <PosList items={s.doesnt} /> : <NegList items={s.doesnt} />}
                  </div>
                )}
              </div>
            )}
            {s.footer && <p className="text-sm mt-4 leading-relaxed font-medium">{s.footer}</p>}
          </div>
        ))}

        <div className="bg-[#0A0A0A] text-white rounded-2xl p-6">
          <h3 className="font-semibold text-lg">Notice</h3>
          <p className="text-white/70 text-sm mt-2 leading-relaxed">
            SelLend is a marketplace that connects users. SelLend is not the owner, buyer, seller, renter, lender, insurer, or guarantor of listed items. Users remain responsible for their own transactions, obligations, and compliance with applicable laws.
          </p>
        </div>

        <p className="text-sm text-muted-foreground">
          Questions about this policy? <Link to="/contact" className="font-semibold text-[#0A0A0A] hover:text-[#FF5A00]">Contact us</Link>.
        </p>
      </section>
    </div>
  );
}
